<template>
  <div class="bg-light min-vh-100 py-3">
    <div class="container d-print-none mb-3">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm me-2"
            @click="$router.back()"
          >
            Back
          </button>
          <button
            type="button"
            class="btn btn-primary btn-sm"
            @click="printPage"
            :disabled="loading || !!error || !order"
          >
            Print
          </button>
        </div>
      </div>
    </div>

    <div class="print-page">
      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div v-else-if="order" class="sheet border rounded">
        <header class="sheet-header d-flex justify-content-between align-items-start mb-3">
          <div class="logo-area">
            <img src="/SWR_Logo.jpg" alt="SWR Logo" style="max-height: 160px;" />
          </div>

          <div class="laptop-info text-end">
            <div class="fw-semibold mb-1">{{ order.device_type }} Information</div>
            <div class="laptop-box">
              <p class="mb-1">
                <strong>Manufacturer:</strong>
                {{ order.device_make }}
              </p>
              <p class="mb-1">
                <strong>Model:</strong>
                {{ order.device_commonname }}
              </p>

              <!-- Serial text + barcode -->
              <div v-if="order.serial_number">
                <p class="mb-1">
                  <strong>Serial Number:</strong>
                  {{ order.serial_number }}
                </p>
                <!-- Code 128 barcode -->
                <div class="mt-1">
                  <svg
                    ref="serialBarcode"
                    class="serial-barcode"
                    aria-label="Serial Number Barcode"
                  ></svg>
                </div>
              </div>
            </div>
          </div>
        </header>

        <!-- Device Specs (from catalog) -->
        <div v-if="order.device_model_details" class="mb-2">
          <div class="fw-semibold small mb-1">Device Specifications</div>
          <table class="table table-bordered table-sm mb-0">
            <tbody>
              <tr v-if="order.device_model_details.cpu">
                <td class="fw-semibold" style="width:30%;">CPU</td>
                <td>{{ order.device_model_details.cpu }}</td>
              </tr>
              <tr v-if="order.device_model_details.ram">
                <td class="fw-semibold">RAM</td>
                <td>{{ order.device_model_details.ram }}</td>
              </tr>
              <tr v-if="order.device_model_details.storage">
                <td class="fw-semibold">Storage</td>
                <td>{{ order.device_model_details.storage }}</td>
              </tr>
              <tr v-if="order.device_model_details.screen_size">
                <td class="fw-semibold">Screen</td>
                <td>{{ order.device_model_details.screen_size }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Customer Info -->
        <div v-if="order.customer_name_display" class="mb-2 small">
          <strong>Customer:</strong> {{ order.customer_name_display }}
        </div>

        <!-- Tracking Info -->
        <div v-if="order.tracking_number" class="mb-2 small">
          <strong>Tracking:</strong>
          {{ order.shipping_carrier ? order.shipping_carrier + ' — ' : '' }}{{ order.tracking_number }}
        </div>

        <!-- Sale Price (SOLD only) -->
        <div v-if="order.order_status === 'SOLD' && order.sold_price" class="mb-2 small">
          <strong>Sale Price:</strong> ${{ Number(order.sold_price).toFixed(2) }}
        </div>

        <main class="sheet-body flex-grow-1">
          <div class="text-center mb-2">
            <span class="fw-semibold">Refurbish Check List</span>
          </div>

          <table class="table table-bordered table-sm mb-0">
            <thead class="table-light">
              <tr>
                <th style="width: 50%;">Item</th>
                <th style="width: 10%;" class="text-center">Completed</th>
                <th style="width: 40%;">Technician Comments</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(step, index) in order.steps"
                :key="step.order_step_id || index"
              >
                <td>
                  {{ step.step_name }}
                </td>
                <td class="text-center align-middle">
                  <span v-if="isStepCompleted(step)">✔</span>
                </td>
                <td class="align-middle">
                  {{ step.notes || step.comment || "" }}
                </td>
              </tr>
            </tbody>
          </table>
        </main>

        <footer class="sheet-footer mt-3 small text-center">
          <p class="mb-1">
            Thank you for your purchase! Please contact us with any questions through
            eBay or by emailing <strong>sales@secondwindrefurbish.com</strong>.
          </p>
          <p class="mb-0">
            For more great laptops at a great price, visit
            <strong>secondwindrefurbish.com</strong>.
          </p>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import { APIService } from "../http/APIService";
import JsBarcode from "jsbarcode";

export default {
  name: "PrintOrder",
  data() {
    return {
      api: new APIService(),
      order: null,
      loading: false,
      error: null,
    };
  },
  created() {
    this.fetchOrder();
  },
  methods: {
    async fetchOrder() {
      this.loading = true;
      this.error = null;

      try {
        const id = this.$route.params.id;
        const accessToken = localStorage.getItem("accessToken");

        const res = await this.api.getOrder(id, accessToken);
        const order = res.data;

        order.steps = Array.isArray(order.steps) ? order.steps : [];
        this.order = order;
      } catch (err) {
        console.error(err);
        this.error = "Unable to load device for printing.";
      } finally {
        this.loading = false;

        this.$nextTick(() => {
          if (!this.error && this.order) {
            // Generate barcode after DOM updated
            this.generateSerialBarcode();

            // Give the browser a moment to render the SVG before printing
            setTimeout(() => {
              window.print();
            }, 1000);
          }
        });
      }
    },

    generateSerialBarcode() {
      if (!this.order.serial_number) return;

      const svg = this.$refs.serialBarcode;
      if (!svg) return;

      try {
        JsBarcode(svg, this.order.serial_number, {
          format: "CODE128",
          displayValue: true,
          fontSize: 12,
          margin: 0,
          width: 1.2,   // bar width
          height: 40,   // bar height
        });
      } catch (e) {
        console.error("Failed to render serial barcode:", e);
      }
    },

    isStepCompleted(step) {
      const status = step.step_status || step.localStatus || step.status || "";
      return status === "PASSED";
    },

    printPage() {
      window.print();
    },
  },
};
</script>

<style scoped>
/* Screen layout */
.print-page {
  max-width: 8.5in;
  margin: 0 auto;
  padding: 0.5rem;
}

.sheet {
  min-height: 8.5in; /* ~11in page minus 2in margins */
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
}

.sheet-header .logo-area {
  font-size: 0.95rem;
}

.logo-img {
  max-height: 60px;
}

/* Laptop info box */
.laptop-info .laptop-box {
  border: 1px solid #ffffff;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
}

/* Barcode styling */
.serial-barcode {
  max-width: 100%;
}

/* Body grows to push footer down */
.sheet-body {
  flex: 1 1 auto;
}

/* Print rules */
@media print {
  @page {
    size: 8.5in 11in;
    margin: 0.5in;
  }

  .bg-light {
    background-color: #ffffff !important;
  }

  .print-page {
    max-width: none;
    margin: 0;
    padding: 0;
  }

  .sheet {
    border: none;
    box-shadow: none;
    padding: 0;
    font-size: 10px;
    page-break-inside: avoid;
  }

  .sheet-header .logo-area img {
    max-height: 80px !important;
  }

  .laptop-info .laptop-box {
    font-size: 9px;
  }

  .sheet .table td,
  .sheet .table th {
    padding: 2px 4px;
  }

  .serial-barcode {
    max-height: 30px;
  }

  .mb-2 {
    margin-bottom: 0.25rem !important;
  }

  .mb-3 {
    margin-bottom: 0.5rem !important;
  }

  .mt-3 {
    margin-top: 0.5rem !important;
  }

  .d-print-none {
    display: none !important;
  }
}
</style>
